import React, { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { TooltipProvider } from "@/components/ui/tooltip"; // Ensure this is imported
import { ProductAlertProvider } from "@/contexts/ProductAlertContext";
import { BundleModalProvider } from "@/contexts/BundleModalContext";
import { ProductModalProvider } from "@/contexts/ProductModalContext";
import { StripeConnectionProvider } from "@/contexts/StripeConnectionContext";
import { ItemCountsProvider } from "@/contexts/ItemCountsContext";
import { GlobalProductAlert } from "@/components/GlobalProductAlert";
import { initializeUTMTracking } from "@/utils/utmTracker";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import CreateUnifiedProduct from "./pages/CreateUnifiedProduct";
import CreateOfferPage from "./pages/CreateOfferPage";
import NotFound from "./pages/NotFound";
import Settings from "./pages/Settings";
import InvoiceViewer from "./pages/InvoiceViewer";
import InvoicePreview from "./pages/InvoicePreview";
import SubscriptionPreview from "./pages/SubscriptionPreview";
import PaymentPage from "./pages/PaymentPage";
import OfferPage from "./pages/OfferPage";
import StripeCallback from "./pages/StripeCallback";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancelled from "./pages/PaymentCancelled";
import ProtectedRoute from "./components/ProtectedRoute";
import DomainRestrictedRoute from "./components/DomainRestrictedRoute";
import PaymentDomainRouter from "./components/PaymentDomainRouter";
import TermsOfService from "./pages/terms";
import PrivacyPolicy from "./pages/privacy";
import PublicPaymentUpdatePage from "./pages/PublicPaymentUpdatePage";
import PaymentUpdateSuccess from "./pages/PaymentUpdateSuccess";

const queryClient = new QueryClient();

const AppLayout = ({ children }) => {
  const location = useLocation();
  const isDashboard = location.pathname === "/dashboard";

  return (
    <div className={`transition-all ${isDashboard ? "ml-[13%]" : "ml-0"}`}>
      {children}
    </div>
  );
};

const App = () => {
  useEffect(() => {
    initializeUTMTracking();
  }, []);
  
  return (
    <React.StrictMode>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
        <TooltipProvider>  {/* Wrap entire app with TooltipProvider */}
          <QueryClientProvider client={queryClient}>
            <StripeConnectionProvider>
              <ItemCountsProvider>
                    <ProductAlertProvider>
                      <BundleModalProvider>
                        <ProductModalProvider>
                          <BrowserRouter>
                    <AppLayout>
                      <Toaster />
                      <Sonner />
                      <GlobalProductAlert />
                      <Routes>
                  <Route path="/" element={
                    <PaymentDomainRouter />
                  } />

                  <Route path="/pay/:id" element={
                    <DomainRestrictedRoute allowedOnPaymentDomain={true}>
                      <PaymentPage />
                    </DomainRestrictedRoute>
                  } />

                  <Route path="/stripe/callback" element={
                    <DomainRestrictedRoute allowedOnPaymentDomain={true}>
                      <StripeCallback />
                    </DomainRestrictedRoute>
                  } />
                  <Route path="/payment-success" element={
                    <DomainRestrictedRoute allowedOnPaymentDomain={true}>
                      <PaymentSuccess />
                    </DomainRestrictedRoute>
                  } />
                  <Route path="/payment-cancelled" element={
                    <DomainRestrictedRoute allowedOnPaymentDomain={true}>
                      <PaymentCancelled />
                    </DomainRestrictedRoute>
                  } />
                  
                  <Route path="/bundle/:id" element={
                    <DomainRestrictedRoute allowedOnPaymentDomain={true} allowedOnAppDomain={false}>
                      <OfferPage />
                    </DomainRestrictedRoute>
                  } />
                  {/* keep the previous route for compatibility */}
                  <Route path="/offer/:id" element={
                    <DomainRestrictedRoute allowedOnPaymentDomain={true} allowedOnAppDomain={false}>
                      <OfferPage />
                    </DomainRestrictedRoute>
                  } />
                  
                  <Route path="/:id" element={
                    <DomainRestrictedRoute allowedOnPaymentDomain={true}>
                      <PaymentPage />
                    </DomainRestrictedRoute>
                  } />
                  
                  <Route path='/auth' element={
                    <DomainRestrictedRoute>
                      <Auth />
                    </DomainRestrictedRoute>
                  } />
                  <Route path="/dashboard" element={
                    <DomainRestrictedRoute>
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    </DomainRestrictedRoute>
                  } />
                  <Route path="/create-product" element={
                    <DomainRestrictedRoute>
                      <ProtectedRoute>
                        <CreateUnifiedProduct />
                      </ProtectedRoute>
                    </DomainRestrictedRoute>
                  } />
                  <Route path="/create-bundle" element={
                    <DomainRestrictedRoute>
                      <ProtectedRoute>
                        <CreateOfferPage />
                      </ProtectedRoute>
                    </DomainRestrictedRoute>
                  } />

                  {/* keep the previous route for compatibility */}
                  <Route path="/create-offer" element={
                    <DomainRestrictedRoute>
                      <ProtectedRoute>
                        <CreateOfferPage />
                      </ProtectedRoute>
                    </DomainRestrictedRoute>
                  } />
                  <Route path="/settings" element={
                    <DomainRestrictedRoute>
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    </DomainRestrictedRoute>
                  } />
                  <Route path="/invoice/:id" element={
                    <DomainRestrictedRoute>
                      <InvoiceViewer />
                    </DomainRestrictedRoute>
                  } />
                  <Route path="/invoice-preview" element={
                    <DomainRestrictedRoute>
                      <InvoicePreview />
                    </DomainRestrictedRoute>
                  } />
                  <Route path="/product-preview" element={
                    <DomainRestrictedRoute>
                      <InvoicePreview />
                    </DomainRestrictedRoute>
                  } />
                  <Route path="/subscription-preview" element={
                    <DomainRestrictedRoute>
                      <SubscriptionPreview />
                    </DomainRestrictedRoute>
                  } />
                  {/* Removed subscription details route */}
                  <Route path="/terms" element={
                    <DomainRestrictedRoute>
                      <TermsOfService />
                    </DomainRestrictedRoute>
                  } />
                  <Route path="/privacy" element={
                    <DomainRestrictedRoute>
                      <PrivacyPolicy />
                    </DomainRestrictedRoute>
                  } />
                  
                  {/* Public payment method update routes */}
                  <Route path="/update-payment/:subscriptionId" element={
                    <DomainRestrictedRoute allowedOnPaymentDomain={true}>
                      <PublicPaymentUpdatePage />
                    </DomainRestrictedRoute>
                  } />
                  <Route path="/payment-method-update/success" element={
                    <DomainRestrictedRoute allowedOnPaymentDomain={true}>
                      <PaymentUpdateSuccess />
                    </DomainRestrictedRoute>
                  } />
                  
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </AppLayout>
                          </BrowserRouter>
                        </ProductModalProvider>
                      </BundleModalProvider>
                    </ProductAlertProvider>
              </ItemCountsProvider>
            </StripeConnectionProvider>
          </QueryClientProvider>
        </TooltipProvider>
      </ThemeProvider>
    </React.StrictMode>
  );
};

export default App;
